// Copyright 2017 Sorama B.V.

import { ISettingsKind } from "datatypes/SettingsKinds/ISettingsKind";
import { URAProperties } from "libraries/Math/URA";

export const RequiredStationaryDuration = 1; // [s]
export const RequiredMinimumMicrophonesInEachDirectionForNearField = 8;
export const PreferredMaximumNearFieldDistance = 0.1; // [m]

export class DistanceKind implements ISettingsKind {
    public name: string;
    public icon: string;
}

export class SourceBehaviorKind implements ISettingsKind {
    public name: string;
    public icon: string;
}

export class ObjectSizeKind implements ISettingsKind {
    public name: string;
    public icon: string;
}

export class RecommendedMeasurementKindSettings {
    // Indicates video is present for a measurement
    readonly hasVideo: boolean;

    readonly distanceKind: DistanceKind;

    readonly sourceBehaviour: SourceBehaviorKind;

    readonly objectSize: ObjectSizeKind;
}

/**
 * A class representing the minimum amount of properties required for checking if a
 * measurement performed with those settings is compatible with a Measurement Kind.
 */
export class MeasurementKindCompatibilitySettings {
    // Indicates video is present for a measurement
    readonly hasVideo: boolean;

    // Duration of a measurement in seconds
    readonly duration: number;

    // The URA properties of the array a measurement was performed with
    readonly channelSelectionUraProperties: URAProperties;

    // The scan width and height of the measurement in amount of arrays
    readonly scanWidthInArrays: number;
    readonly scanHeightInArrays: number;
}

export class MeasurementKindRecommendedSettings extends MeasurementKindCompatibilitySettings {
    // The distance between the SoundCamera and the object
    readonly distance: number;
}

export class MeasurementKindCompatibilityProperties {
    isCompatible: boolean;
    reason: string;
}

export class MeasurementKind implements ISettingsKind {
    public name: string;
    public icon: string;
    public distanceKind: DistanceKind;
    public sourceBehavior: SourceBehaviorKind;
    public getCompatibilityWith: (
        settings: MeasurementKindCompatibilitySettings
    ) => MeasurementKindCompatibilityProperties;
    public isRecommendedFor: (settings: MeasurementKindRecommendedSettings) => boolean;
}

export const DistanceKinds = {
    FarField: <DistanceKind>{ name: "Far Field", icon: "/resources/images/icons/FarField.svg" },
    NearField: <DistanceKind>{ name: "Near Field", icon: "/resources/images/icons/NearField.svg" },
    All: function (): DistanceKind[] {
        return [this.FarField, this.NearField];
    },
};

export const SourceBehaviors = {
    Stationary: <SourceBehaviorKind>{ name: "Stationary", icon: "/resources/images/icons/Stationary.svg" },
    Transient: <SourceBehaviorKind>{ name: "Transient", icon: "/resources/images/icons/Transient.svg" },
    All: function (): ObjectSizeKind[] {
        return [this.Stationary, this.Transient];
    },
};

export const ObjectSizes = {
    Small: <ObjectSizeKind>{ name: "Small", icon: "/resources/images/icons/SmallObject.svg" },
    Large: <ObjectSizeKind>{ name: "Large", icon: "/resources/images/icons/LargeObject.svg" },
    All: function (): ObjectSizeKind[] {
        return [this.Small, this.Large];
    },
};

/**
 * Measurement Kind Definition for Beamforming
 * NOTE: the function(settings){} syntax is REQUIRED for the "getCompatibilityWith" and "isRecommendedFor" methods
 * in order to properly capture the "this". Using a Lambda settings => {} does NOT capture the correct "this"!
 */
const BeamformingKind = <MeasurementKind>{
    name: "Beamforming",
    icon: "/resources/images/icons/Beamforming.svg",
    distanceKind: DistanceKinds.FarField,
    sourceBehavior: SourceBehaviors.Transient,
    getCompatibilityWith: function (settings) {
        if (!settings.hasVideo) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason: this.name + " requires a video recording, which is not present.",
            };
        }

        if (settings.scanWidthInArrays > 1 || settings.scanHeightInArrays > 1) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason: this.name + " can not be used when scanning.",
            };
        }

        return <MeasurementKindCompatibilityProperties>{
            isCompatible: true,
        };
    },
    isRecommendedFor: function (settings) {
        return this.getCompatibilityWith(settings).isCompatible
            ? !settings.channelSelectionUraProperties.isUra || settings.distance > PreferredMaximumNearFieldDistance
            : false;
    },
};

/**
 * Measurement Kind Definition for Stationary Holography
 * NOTE: the function(settings){} syntax is REQUIRED for the "getCompatibilityWith" and "isRecommendedFor" methods
 * in order to properly capture the "this". Using a Lambda settings => {} does NOT capture the correct "this"!
 */
const StationaryHolographyKind = <MeasurementKind>{
    name: "Stationary Holography",
    icon: "/resources/images/icons/StationaryHolography.svg",
    distanceKind: DistanceKinds.NearField,
    sourceBehavior: SourceBehaviors.Stationary,
    getCompatibilityWith: function (settings) {
        const scanLeastNrOfElements = Math.min(
            settings.channelSelectionUraProperties.widthInElements * settings.scanWidthInArrays,
            settings.channelSelectionUraProperties.heightInElements * settings.scanHeightInArrays
        );

        if (!settings.channelSelectionUraProperties.isUra) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason: this.name + " requires an Uniform Rectangular Array (URA) microphone selection.",
            };
        }

        if (settings.duration != 1) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason: this.name + " requires a measurement duration of exactly 1 second.",
            };
        }

        if (scanLeastNrOfElements < RequiredMinimumMicrophonesInEachDirectionForNearField) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason:
                    this.name +
                    " requires a minimum of " +
                    RequiredMinimumMicrophonesInEachDirectionForNearField +
                    " microphones in each dimension (X and Y).",
            };
        }

        return <MeasurementKindCompatibilityProperties>{
            isCompatible: true,
        };
    },
    isRecommendedFor: function (settings) {
        return this.getCompatibilityWith(settings).isCompatible
            ? !BeamformingKind.isRecommendedFor(settings) &&
                  (settings.scanWidthInArrays > 1 ||
                      settings.scanHeightInArrays > 1 ||
                      settings.distance <= PreferredMaximumNearFieldDistance ||
                      settings.duration === RequiredStationaryDuration)
            : false;
    },
};

/**
 * Measurement Kind Definition for Transient Holography
 * NOTE: the function(settings){} syntax is REQUIRED for the "getCompatibilityWith" and "isRecommendedFor" methods
 * in order to properly capture the "this". Using a Lambda settings => {} does NOT capture the correct "this"!
 */
const TransientHolographyKind = <MeasurementKind>{
    name: "Transient Holography",
    icon: "/resources/images/icons/TransientHolography.svg",
    distanceKind: DistanceKinds.NearField,
    sourceBehavior: SourceBehaviors.Transient,
    getCompatibilityWith: function (settings) {
        const scanLeastNrOfElements = Math.min(
            settings.channelSelectionUraProperties.widthInElements * settings.scanWidthInArrays,
            settings.channelSelectionUraProperties.heightInElements * settings.scanHeightInArrays
        );

        if (!settings.channelSelectionUraProperties.isUra) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason: this.name + " requires an Uniform Rectangular Array (URA) microphone selection.",
            };
        }

        if (settings.scanWidthInArrays > 1 || settings.scanHeightInArrays > 1) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason: this.name + " can not be used when scanning.",
            };
        }

        if (scanLeastNrOfElements < RequiredMinimumMicrophonesInEachDirectionForNearField) {
            return <MeasurementKindCompatibilityProperties>{
                isCompatible: false,
                reason:
                    "A minimum of " +
                    RequiredMinimumMicrophonesInEachDirectionForNearField +
                    " microphones is required in each dimension (X and Y).",
            };
        }

        return <MeasurementKindCompatibilityProperties>{
            isCompatible: true,
        };
    },
    isRecommendedFor: function (settings) {
        return this.getCompatibilityWith(settings).isCompatible
            ? !BeamformingKind.isRecommendedFor(settings) && settings.duration != RequiredStationaryDuration
            : false;
    },
};

/**
 * All measurements kinds currently suported by the analysis tools
 */
export const MeasurementKinds = {
    Beamforming: BeamformingKind,
    StationaryHolography: StationaryHolographyKind,
    TransientHolography: TransientHolographyKind,
    All: function (): MeasurementKind[] {
        return [this.Beamforming, this.StationaryHolography, this.TransientHolography];
    },
    Recommended(measurementKindCompatibilitySettings: RecommendedMeasurementKindSettings): MeasurementKind {
        if (
            measurementKindCompatibilitySettings.distanceKind == DistanceKinds.FarField &&
            measurementKindCompatibilitySettings.hasVideo
        ) {
            return MeasurementKinds.Beamforming;
        } else if (
            measurementKindCompatibilitySettings.distanceKind == DistanceKinds.NearField &&
            measurementKindCompatibilitySettings.sourceBehaviour == SourceBehaviors.Stationary
        ) {
            return MeasurementKinds.StationaryHolography;
        } else if (
            measurementKindCompatibilitySettings.distanceKind == DistanceKinds.NearField &&
            measurementKindCompatibilitySettings.sourceBehaviour == SourceBehaviors.Transient &&
            measurementKindCompatibilitySettings.objectSize != ObjectSizes.Large
        ) {
            return MeasurementKinds.TransientHolography;
        }

        return null;
    },
};
